/**
 * Die Navigation des Leitstands — drei Bereiche nach Tätigkeit.
 *
 * Heute sind die rund fünfzig Verwaltungsseiten nach Datenart gruppiert
 * ("Stammdaten", "Maschinen", "Produktionseinstellungen") — also so, wie die
 * Datenbank aufgebaut ist. Diese Gliederung fragt stattdessen, was jemand
 * gerade tun will:
 *
 *   Auswerten   Zahlen ansehen und vergleichen
 *   Betrieb     der laufende Tag: Schichten, Störungen, Meldungen
 *   Einrichten  konfigurieren: Stammdaten, Geräte, Benutzer
 *
 * Ob die Dreiteilung trägt, ist laut docs/OBERFLAECHE-ENTSCHEIDUNGEN.md noch
 * offen — "Betrieb" ist der wackligste Bereich. Diese Datei ist die eine
 * Stelle, an der sich das ändern lässt.
 *
 * Aufgeführt sind nur die Einstiegsseiten. Detailseiten (`/admin/kunden/:id`)
 * erreicht man über ihre Liste, nicht über das Menü.
 */

export interface Seite {
  name: string;
  pfad: string;
}

export interface Bereich {
  schluessel: 'auswerten' | 'betrieb' | 'einrichten';
  name: string;
  seiten: Seite[];
}

export const BEREICHE: Bereich[] = [
  {
    schluessel: 'auswerten',
    name: 'Auswerten',
    seiten: [
      { name: 'Taktwaschanlagen', pfad: '/reports/twa' },
      { name: 'Waschleistung', pfad: '/reports/wash-performance' },
      { name: 'Waschschleuderautomaten', pfad: '/reports/wsa' },
      { name: 'Fertigstellung', pfad: '/reports/processing' },
      { name: 'Störungen', pfad: '/reports/disruptions' },
    ],
  },
  {
    schluessel: 'betrieb',
    name: 'Betrieb',
    seiten: [
      { name: 'Heute', pfad: '/admin/heute' },
      { name: 'Schichten', pfad: '/admin/shifts' },
      { name: 'Störungen', pfad: '/admin/disruptions' },
      { name: 'Korrekturen', pfad: '/admin/corrections' },
      { name: 'Meldungen', pfad: '/admin/feedback' },
      { name: 'Protokoll', pfad: '/admin/audit' },
    ],
  },
  {
    schluessel: 'einrichten',
    name: 'Einrichten',
    seiten: [{ name: 'Katalog', pfad: '/einrichten' }],
  },
];

/**
 * Findet den Bereich, zu dem ein Pfad gehört. Trifft auch Detailseiten,
 * weil auf Präfix verglichen wird: `/admin/customers/abc` gehört zu
 * `/admin/customers`.
 *
 * Der längste Treffer gewinnt — sonst würde `/admin/disruption-categories`
 * fälschlich unter `/admin/disruptions` einsortiert.
 */
export function findeBereich(pfad: string): Bereich | null {
  let treffer: { bereich: Bereich; laenge: number } | null = null;

  for (const bereich of BEREICHE) {
    for (const seite of bereich.seiten) {
      const passt = pfad === seite.pfad || pfad.startsWith(`${seite.pfad}/`);
      if (passt && (treffer === null || seite.pfad.length > treffer.laenge)) {
        treffer = { bereich, laenge: seite.pfad.length };
      }
    }
  }

  if (treffer) return treffer.bereich;

  // Rest der alten Verwaltungswelt ohne eigenen Menü-Eintrag (Kunden, Benutzer,
  // Stationen … bis P4–P6): Heimat ist Einrichten — erreichbar über den Katalog.
  if (pfad.startsWith('/admin')) {
    return BEREICHE.find((b) => b.schluessel === 'einrichten') ?? null;
  }
  return null;
}

/** Die Seite, auf der man gerade steht — für die Hervorhebung in der Unterleiste. */
export function findeSeite(pfad: string): Seite | null {
  let treffer: Seite | null = null;

  for (const bereich of BEREICHE) {
    for (const seite of bereich.seiten) {
      const passt = pfad === seite.pfad || pfad.startsWith(`${seite.pfad}/`);
      if (passt && (treffer === null || seite.pfad.length > treffer.pfad.length)) {
        treffer = seite;
      }
    }
  }

  return treffer;
}
